import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { X, Save, User as UserIcon, Loader } from 'lucide-react';
import Select from 'react-select';
import { User, Industry, Company } from './../types/user';

interface UserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (user: { name: string; email: string; industry: string; distributor: string }) => void;
  user?: User;
  industries: Industry[];
  distributors: Company[];
  isSubmitting?: boolean;
}

interface SelectOption {
  value: number;
  label: string;
}

const ALL_VALUE = -9999;
const ALL_OPTION: SelectOption = { value: ALL_VALUE, label: 'Todos' };

export default function UserModal({ 
  isOpen, 
  onClose, 
  onSave, 
  user, 
  industries, 
  distributors, 
  isSubmitting = false 
}: UserModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    industry: '',
    distributor: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [selectedIndustries, setSelectedIndustries] = useState<SelectOption[]>([]);
  const [selectedDistributors, setSelectedDistributors] = useState<SelectOption[]>([]);
  const [isIndustryDropdownOpen, setIsIndustryDropdownOpen] = useState(false);
  const [isDistributorDropdownOpen, setIsDistributorDropdownOpen] = useState(false);

  // Memoizar as opções para evitar re-renders desnecessários
  const industryOptions = useMemo<SelectOption[]>(() => [
    ALL_OPTION,
    ...industries.map((ind:any) => ({ value: ind.value, label: ind.label }))
  ], [industries]);

  const distributorOptions = useMemo<SelectOption[]>(() => [
    ALL_OPTION,
    ...distributors.map((dist:any) => ({ value: dist.value, label: dist.label }))
  ], [distributors]);

  // Função para normalizar strings para comparação
  const normalizeString = useCallback((str: string): string => {
    return str
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove acentos
      .replace(/[^\w\s]/g, '') // Remove pontuação
      .trim();
  }, []);

  // Processar conteúdo da área de transferência para indústrias
  const processIndustryClipboardContent = useCallback((content: string): SelectOption[] => {
    const pastedIndustries: SelectOption[] = [];
    
    const industryNames = content.split('\n').map(name => name.trim()).filter(name => name.length > 0)
      .concat(content.split(' - ').map(name => name.trim()));
    
    industryNames.forEach(pastedName => {
      const normalizedPasted = normalizeString(pastedName);
      
      industryOptions.forEach(industry => {
        const normalizedIndustry = normalizeString(industry.label);
        
        // Verifica correspondência exata
        if (normalizedPasted === normalizedIndustry) {
          pastedIndustries.push(industry);
          return;
        }
        
        // Verifica correspondência parcial
        if (normalizedIndustry.includes(normalizedPasted) || normalizedPasted.includes(normalizedIndustry)) {
          pastedIndustries.push(industry);
        }
      });
    });
    
    // Remove duplicatas
    const uniqueIndustries = pastedIndustries.filter((industry, index, self) =>
      index === self.findIndex(i => i.value === industry.value)
    );
    
    return uniqueIndustries;
  }, [industryOptions, normalizeString]);

  // Processar conteúdo da área de transferência para distribuidores
  const processDistributorClipboardContent = useCallback((content: string): SelectOption[] => {
    const pastedDistributors: SelectOption[] = [];
    
    const distributorNames = content.split('\n').map(name => name.trim()).filter(name => name.length > 0)
      .concat(content.split(' - ').map(name => name.trim()));
    
    distributorNames.forEach(pastedName => {
      const normalizedPasted = normalizeString(pastedName);
      
      distributorOptions.forEach(distributor => {
        const normalizedDistributor = normalizeString(distributor.label);
        
        // Verifica correspondência exata
        if (normalizedPasted === normalizedDistributor) {
          pastedDistributors.push(distributor);
          return;
        }
        
        // Para casos específicos, verifica correspondência mais rigorosa
        const pastedParts = normalizedPasted.split(/\s+/);
        const distributorParts = normalizedDistributor.split(/\s+/);
        
        const allPartsMatch = pastedParts.every(pastedPart => 
          distributorParts.some(distributorPart => 
            distributorPart === pastedPart || 
            (pastedPart.length > 2 && distributorPart.includes(pastedPart)) ||
            (distributorPart.length > 2 && pastedPart.includes(distributorPart))
          )
        );
        
        if (allPartsMatch && pastedParts.length >= 1) {
          pastedDistributors.push(distributor);
        }
      });
    });
    
    // Remove duplicatas
    const uniqueDistributors = pastedDistributors.filter((distributor, index, self) =>
      index === self.findIndex(d => d.value === distributor.value)
    );
    
    return uniqueDistributors;
  }, [distributorOptions, normalizeString]);

  // Listener para Ctrl+V - Indústrias
  useEffect(() => {
    const handleKeyDown = async (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === 'v' && isIndustryDropdownOpen) {
        e.preventDefault();
        
        try {
          const text = await navigator.clipboard.readText();
          const matchedIndustries = processIndustryClipboardContent(text);
          
          if (matchedIndustries.length > 0) {
            const currentValues = selectedIndustries.map(i => i.value);
            const newIndustries = matchedIndustries.filter(i => !currentValues.includes(i.value));
            const updatedIndustries = [...selectedIndustries, ...newIndustries];
            setSelectedIndustries(updatedIndustries);
            
            const values = updatedIndustries.map(option => option.value).join(',');
            setFormData(prev => ({ ...prev, industry: values }));
            
            console.log(`${matchedIndustries.length} indústrias selecionadas automaticamente`);
          } else {
            console.log('Nenhuma indústria encontrada no texto colado');
          }
        } catch (err) {
          console.error('Erro ao acessar clipboard:', err);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isIndustryDropdownOpen, selectedIndustries, processIndustryClipboardContent]);

  // Listener para Ctrl+V - Distribuidores
  useEffect(() => {
    const handleKeyDown = async (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === 'v' && isDistributorDropdownOpen) {
        e.preventDefault();
        
        try {
          const text = await navigator.clipboard.readText();
          const matchedDistributors = processDistributorClipboardContent(text);
          
          if (matchedDistributors.length > 0) {
            const currentValues = selectedDistributors.map(d => d.value);
            const newDistributors = matchedDistributors.filter(d => !currentValues.includes(d.value));
            const updatedDistributors = [...selectedDistributors, ...newDistributors];
            setSelectedDistributors(updatedDistributors);
            
            const values = updatedDistributors.map(option => option.value).join(',');
            setFormData(prev => ({ ...prev, distributor: values }));
            
            console.log(`${matchedDistributors.length} distribuidores selecionados automaticamente`);
          } else {
            console.log('Nenhum distribuidor encontrado no texto colado');
          }
        } catch (err) {
          console.error('Erro ao acessar clipboard:', err);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isDistributorDropdownOpen, selectedDistributors, processDistributorClipboardContent]);

  // Estilos customizados para o react-select
  const customSelectStyles = useMemo(() => ({
    control: (provided: any, state: any) => ({
      ...provided,
      borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(59, 130, 246, 0.2)' : 'none',
      '&:hover': {
        borderColor: '#3b82f6'
      }
    }),
    menu: (provided: any) => ({
      ...provided,
      zIndex: 9999
    }),
    menuPortal: (provided: any) => ({
      ...provided,
      zIndex: 9999
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#3b82f6' : state.isFocused ? '#eff6ff' : 'white',
      color: state.isSelected ? 'white' : '#374151'
    })
  }), []);

  // Resetar formulário quando modal abre/fecha ou usuário muda
  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name,
        email: user.email,
        industry: user.industries,
        distributor: user.companies
      });
      
      // Definir valores selecionados para os selects
      const industryIds = user.industries.split(',').filter(id => id.trim()).map(id => parseInt(id.trim()));
      const distributorIds = user.companies.split(',').filter(id => id.trim()).map(id => parseInt(id.trim()));
      
      const selectedIndustriesOptions = industryOptions.filter(opt => industryIds.includes(parseInt(opt.value.toString())));
      const selectedDistributorsOptions = distributorOptions.filter(opt => distributorIds.includes(parseInt(opt.value.toString())));
      setSelectedIndustries(selectedIndustriesOptions);
      setSelectedDistributors(selectedDistributorsOptions);
    } else {
      setFormData({
        name: '',
        email: '',
        industry: '',
        distributor: ''
      });
      setSelectedIndustries([]);
      setSelectedDistributors([]);
    }
    setErrors({});
  }, [user, isOpen, industryOptions, distributorOptions]);

  const validateForm = useCallback(() => {
    const errs: Record<string, string> = {};
    if (!formData.name.trim()) errs.name = 'Nome é obrigatório';
    if (!formData.email.trim()) errs.email = 'E-mail é obrigatório';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errs.email = 'E-mail inválido';
    if (!formData.industry) errs.industry = 'Selecione ao menos uma indústria';
    if (!formData.distributor) errs.distributor = 'Selecione ao menos um distribuidor';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }, [formData]);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      // Garantir que campos vazios sejam enviados como string vazia
      const dataToSave = {
        ...formData,
        industry: formData.industry || '',
        distributor: formData.distributor || ''
      };
      onSave(dataToSave);
    }
  }, [formData, validateForm, onSave]);

  const handleChange = useCallback((field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  }, [errors]);

  const handleIndustryChange = useCallback((selected: SelectOption[] | any) => {
    const options = selected || [];
    // Se selecionar 'Todos', mantém apenas ele
    if (options.some((o:any) => o.value === ALL_VALUE)) {
      setSelectedIndustries([ALL_OPTION]);
      setFormData(prev => ({ ...prev, industry: ALL_VALUE.toString() }));
    } else {
      // Remove 'Todos' caso estivesse selecionado
      const filtered = options.filter((o:any) => o.value !== ALL_VALUE);
      setSelectedIndustries(filtered);
      const values = filtered.map((o:any) => o.value).join(',');
      setFormData(prev => ({ ...prev, industry: values }));
    }
  }, []);

  // Manipulador de mudança para Distribuidor
  const handleDistributorChange = useCallback((selected: SelectOption[] | any) => {
    const options = selected || [];
    if (options.some((o:any) => o.value === ALL_VALUE)) {
      setSelectedDistributors([ALL_OPTION]);
      setFormData(prev => ({ ...prev, distributor: ALL_VALUE.toString() }));
    } else {
      const filtered = options.filter((o:any) => o.value !== ALL_VALUE);
      setSelectedDistributors(filtered);
      const values = filtered.map((o:any) => o.value).join(',');
      setFormData(prev => ({ ...prev, distributor: values }));
    }
  }, []);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <UserIcon className="w-5 h-5 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900">
              {user ? 'Editar Usuário' : 'Novo Usuário'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nome *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.name ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Digite o nome completo"
            />
            {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              E-mail *
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.email ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="usuario@empresa.com"
            />
            {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Indústria
            </label>
            <Select
              value={selectedIndustries}
              isMulti
              onMenuOpen={() => setIsIndustryDropdownOpen(true)}
              onMenuClose={() => setIsIndustryDropdownOpen(false)}
              onChange={handleIndustryChange}
              options={industryOptions}
              placeholder={isIndustryDropdownOpen ? "Selecione as indústrias (Ctrl+V para colar)" : "Selecione as indústrias"}
              styles={customSelectStyles}
              className="text-sm"
              isClearable
              menuPortalTarget={document.body}
              menuPosition="fixed"
              isSearchable
            />
            {errors.industry && <p className="mt-1 text-sm text-red-600">{errors.industry}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Distribuidor
            </label>
            <Select
              value={selectedDistributors}
              isMulti
              onMenuOpen={() => setIsDistributorDropdownOpen(true)}
              onMenuClose={() => setIsDistributorDropdownOpen(false)}
              onChange={handleDistributorChange}
              options={distributorOptions}
              placeholder={isDistributorDropdownOpen ? "Selecione os distribuidores (Ctrl+V para colar)" : "Selecione os distribuidores"}
              styles={customSelectStyles}
              className="text-sm"
              isClearable
              menuPortalTarget={document.body}
              menuPosition="fixed"
              isSearchable
            />
            {errors.distributor && <p className="mt-1 text-sm text-red-600">{errors.distributor}</p>}
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:bg-blue-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors flex items-center justify-center space-x-2"
            >
              {isSubmitting ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  <span>Salvando...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>{user ? 'Salvar' : 'Criar'}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}